import { useState } from 'react';

import { cn } from '../../../shared/lib/cn';
import { GROUP_LABEL } from '../../../shared/ui/group-label';
import { ChevronDownIcon, ClockIcon } from '../../../shared/ui/icons';
import { Popover, PopoverContent, PopoverTrigger } from '../../../shared/ui/popover';
import type { TRetentionPeriod } from '../api';
import { RETENTION_PERIODS, coversRetries, periodLabel } from '../lib';

interface IPeriodPickerProps {
  value: TRetentionPeriod;
  /** Beside it: "set here", "from Outreach", "this flow", "default". */
  source: string;
  own: TRetentionPeriod | null;
  /** "Follow Outreach": clearing its own period; absent where there is nothing to follow. */
  follow?: { label: string; period: TRetentionPeriod };
  holdSeconds?: number | null;
  title?: string;
  warning?: string | null;
  caution?: boolean;
  disabled?: boolean;
  onChoose: (period: TRetentionPeriod | null) => void;
  'aria-label': string;
}

const Choice = ({
  label,
  note,
  selected,
  onClick,
}: {
  label: string;
  note?: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    role="menuitemradio"
    aria-checked={selected}
    onClick={onClick}
    className={cn(
      'flex h-control-sm w-full items-center gap-3 rounded-item px-inset text-left text-data',
      selected ? 'bg-highlight text-primary' : 'text-primary hover:bg-hover-raised',
    )}
  >
    <span className="min-w-0 flex-1 truncate">{label}</span>
    {note && <span className="shrink-0 font-mono text-micro text-faint">{note}</span>}
  </button>
);

export const PeriodPicker = ({
  value,
  source,
  own,
  follow,
  holdSeconds = null,
  title,
  warning,
  caution,
  disabled,
  onChoose,
  ...rest
}: IPeriodPickerProps) => {
  const [open, setOpen] = useState(false);
  const choose = (period: TRetentionPeriod | null) => {
    setOpen(false);
    if (period !== own) onChoose(period);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          aria-label={rest['aria-label']}
          className={cn(
            'inline-flex h-control-xs max-w-full items-center gap-label rounded-control border bg-field px-inset',
            'text-primary disabled:text-muted',
            caution ? 'border-caution-strong' : 'border-border-raised',
          )}
        >
          <span className="font-mono text-meta font-medium">{periodLabel(value)}</span>
          <span className="truncate text-micro text-muted">{source}</span>
          <ChevronDownIcon size="sm" className="text-muted" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-period-menu p-label" role="menu">
        {title && <p className={cn('px-inset pt-label pb-2', GROUP_LABEL)}>{title}</p>}
        {follow && (
          <>
            <Choice
              label={follow.label}
              note={`${periodLabel(follow.period)}${
                coversRetries(follow.period, holdSeconds) ? ' · covers its retries' : ''
              }`}
              selected={own === null}
              onClick={() => choose(null)}
            />
            <div className="my-label h-px bg-border-raised" />
          </>
        )}
        {RETENTION_PERIODS.map((period) => (
          <Choice
            key={period}
            label={periodLabel(period)}
            note={
              period === 'forever'
                ? 'never cleaned up'
                : coversRetries(period, holdSeconds)
                  ? undefined
                  : 'shorter than its retries'
            }
            selected={own === period || (!follow && value === period)}
            onClick={() => choose(period)}
          />
        ))}
        {warning && (
          <p className="mt-label flex gap-2 px-inset pb-label text-meta leading-17 text-primary">
            <ClockIcon size="sm" className="mt-hair text-muted" />
            <span>{warning}</span>
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};
